"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createReportRenderer = createReportRenderer;
// Helper to filter ReferenceSpans from a block's items
const filterReferenceSpansFromBlock = (block) => {
    if (block.data.kind === 'text' || block.data.kind === 'heading') {
        const originalItems = block.data.items ?? [];
        const filteredItems = originalItems.filter((item) => item.kind !== 'reference');
        // Return a new block object with filtered items
        return {
            ...block,
            data: {
                ...block.data,
                items: filteredItems,
            },
        };
    }
    // Return other block types unchanged
    return block;
};
// Helper to get reference IDs from a BlockObject's items
const getReferenceIdsFromItems = (blockObject) => {
    const blockData = blockObject.data;
    if (blockData.kind !== 'text' && blockData.kind !== 'heading') {
        return [];
    }
    // Extract IDs from reference spans within items
    return (blockData.items
        ?.filter((item) => item.kind === 'reference')
        .map((ref) => ref.id) ?? []);
};
// REMOVED: createDereferencedFootnotes function
// --- Validation Function ---
// Simplified validation - only checks for summary after H1
function runReportRendererValidation(title, rootSubtitle, summary, introductionBlocks) {
    const specificIssues = [];
    const hasH1 = introductionBlocks.some((b) => b.data.kind === 'heading' && b.data.level === 1);
    const firstH1Index = introductionBlocks.findIndex((b) => b.data.kind === 'heading' && b.data.level === 1);
    if (hasH1 && !summary) {
        const hasTextAfterH1 = introductionBlocks
            .slice(firstH1Index + 1)
            .some((b) => b.data.kind === 'text');
        if (!hasTextAfterH1) {
            specificIssues.push({
                level: 'warning',
                message: 'Rendered report data missing summary (expected text block after H1).',
                index: null,
            });
        }
    }
    return specificIssues;
}
// --- Renderer Implementation ---
function createReportRenderer() {
    return {
        render(props) {
            const originalBlocks = props.blocks;
            let contentBlocks = originalBlocks; // Start with all blocks
            // REMOVED: Footnote definition map building
            // Filter footnote blocks from content processing (but keep for reference gathering if needed - though currently not needed)
            // contentBlocks = originalBlocks.filter((b) => b.data.kind !== 'footnote'); // Re-enable if direct footnote block handling is needed
            // Check for leading callout caption
            let extractedCaption = undefined;
            let hasRootCaption = false;
            if (contentBlocks.length > 0 && contentBlocks[0]?.data.kind === 'callout') {
                extractedCaption = contentBlocks[0].data.text ?? '';
                hasRootCaption = true;
                contentBlocks = contentBlocks.slice(1); // Consume caption block
            }
            // Parse blocks into intro and sections
            const introductionBlocks = [];
            const parsedSections = [];
            let currentParsedSection = null;
            let processingIntro = true;
            let firstH1Processed = false; // Flag to track the main title H1
            for (const block of contentBlocks) {
                const blockData = block.data;
                const referencesFromItems = getReferenceIdsFromItems(block);
                let isSectionStarter = false;
                // Check if the block is a heading that should start a section
                if (blockData.kind === 'heading') {
                    const level = blockData.level;
                    if (level === 1) {
                        if (!firstH1Processed) {
                            // This is the TITLE H1. Mark as processed, but DON'T start section.
                            firstH1Processed = true;
                        }
                        else {
                            // Subsequent H1 - starts a section
                            isSectionStarter = true;
                        }
                    }
                    else if (level === 2 || level === 3) {
                        // H2 or H3 always start a section
                        isSectionStarter = true;
                        // If we see an H2/H3 before the main H1 (unlikely but possible), mark H1 as processed.
                        if (!firstH1Processed)
                            firstH1Processed = true;
                    }
                }
                // Determine where to put the block based on whether it starts a section
                if (isSectionStarter) {
                    processingIntro = false; // We are definitely out of the intro now
                    if (currentParsedSection) {
                        parsedSections.push(currentParsedSection);
                    }
                    // Start the new section - Ensure blockData is HeadingBlock here
                    if (blockData.kind === 'heading') {
                        currentParsedSection = {
                            id: block.id,
                            title: blockData.items // Now safe to access
                                ?.flatMap((item) => {
                                if (item.kind === 'text') {
                                    return [item.text];
                                }
                                return [];
                            })
                                .join('') ?? '',
                            level: blockData.level, // Cast to number
                            blocks: [],
                            subtitle: '',
                            referencedIds: new Set(referencesFromItems),
                        };
                    }
                    else {
                        // This path should ideally not be reached if isSectionStarter is true
                        // Handle error or unexpected state if necessary
                        console.error('Unexpected block type marked as section starter');
                        currentParsedSection = null; // Avoid potential issues
                    }
                    // The heading itself is consumed as the section title/metadata
                }
                else if (processingIntro) {
                    // Still processing the introduction (before the first section-starting heading)
                    const filteredBlock = filterReferenceSpansFromBlock(block);
                    introductionBlocks.push(filteredBlock);
                    // If we just processed the main title H1, stop processing intro on the NEXT block
                    // unless the next block is the root subtitle callout or summary text.
                    // This check is implicitly handled by how title/subtitle/summary are extracted later.
                }
                else if (currentParsedSection) {
                    // Belongs to the current section
                    referencesFromItems.forEach((refId) => currentParsedSection.referencedIds.add(refId));
                    const isSubtitleCallout = !hasRootCaption &&
                        blockData.kind === 'callout' &&
                        !currentParsedSection.subtitle;
                    if (isSubtitleCallout) {
                        currentParsedSection.subtitle =
                            blockData.text ?? '';
                        // Subtitle callout is consumed, not added to blocks
                    }
                    else {
                        // Add block (with references filtered) to the current section
                        const filteredBlock = filterReferenceSpansFromBlock(block);
                        currentParsedSection.blocks.push(filteredBlock);
                    }
                } // else: This case should ideally not be reached if logic is correct
            }
            // Push the last processed section
            if (currentParsedSection) {
                parsedSections.push(currentParsedSection);
            }
            // Derive root Title, Subtitle, and Summary
            let title = '';
            const firstH1BlockObject = introductionBlocks.find((b) => b.data.kind === 'heading' && b.data.level === 1);
            if (firstH1BlockObject) {
                title =
                    firstH1BlockObject.data.items
                        ?.flatMap((item) => {
                        if (item.kind === 'text') {
                            return [item.text];
                        }
                        return [];
                    })
                        .join('') ?? '';
            }
            let rootSubtitle = extractedCaption ?? '';
            if (!rootSubtitle) {
                const firstH1Index = introductionBlocks.findIndex((b) => b.data.kind === 'heading' && b.data.level === 1);
                const searchStartIndex = firstH1Index !== -1 ? firstH1Index + 1 : 0;
                const introCalloutBlockObject = introductionBlocks
                    .slice(searchStartIndex)
                    .find((b) => b.data.kind === 'callout');
                if (introCalloutBlockObject) {
                    rootSubtitle =
                        introCalloutBlockObject.data.text ?? '';
                }
            }
            let summary = '';
            const firstH1IndexSummary = introductionBlocks.findIndex((b) => b.data.kind === 'heading' && b.data.level === 1);
            const searchStartIndexSummary = firstH1IndexSummary !== -1 ? firstH1IndexSummary + 1 : 0;
            const firstTextBlockObject = introductionBlocks
                .slice(searchStartIndexSummary)
                .find((b) => b.data.kind === 'text');
            if (firstTextBlockObject) {
                summary =
                    firstTextBlockObject.data.items
                        ?.flatMap((item) => {
                        if (item.kind === 'text') {
                            return [item.text];
                        }
                        return [];
                    })
                        .join('') ?? '';
            }
            // Run Validation
            const issues = runReportRendererValidation(title, rootSubtitle, summary, introductionBlocks);
            // Map parsed sections and Collect ALL unique reference IDs
            const allReferencedIdsAcrossSections = new Set();
            const finalSections = parsedSections.map((sec) => {
                sec.referencedIds.forEach((id) => allReferencedIdsAcrossSections.add(id));
                // Map IDs to ReferenceSpan objects for the section
                const sectionReferences = Array.from(sec.referencedIds).map((id) => ({
                    kind: 'reference',
                    id,
                }));
                return {
                    id: sec.id,
                    title: sec.title,
                    subtitle: sec.subtitle,
                    blocks: sec.blocks,
                    references: sectionReferences,
                };
            });
            let status = 'success';
            if (issues.some((issue) => issue.level === 'error')) {
                status = 'error';
            }
            else if (issues.length > 0) {
                status = 'warning';
            }
            // Map aggregated IDs to ReferenceSpan objects for top-level references
            const finalReferences = Array.from(allReferencedIdsAcrossSections).map((id) => ({
                kind: 'reference',
                id,
            }));
            return {
                output: {
                    title,
                    subtitle: rootSubtitle,
                    number: props.content.ordinal,
                    summary,
                    sections: finalSections,
                },
                issues,
                status,
                references: finalReferences,
            };
        },
    };
}
//# sourceMappingURL=reportRendererProvider.js.map